/**
 * Enforcement Journey Widget
 *
 * Visual pipeline visualization showing progress toward DMARC enforcement.
 * Shows domains at each stage (None → Quarantine → Reject) with smart
 * next-step recommendations.
 */

import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Shield,
  ShieldCheck,
  ShieldAlert,
  ArrowRight,
  Lightbulb,
  HelpCircle,
  ChevronRight,
} from "lucide-react";
import { api, EnforcementReadinessResponse } from "@/lib/api";

interface EnforcementJourneyWidgetProps {
  /** Optional: Preloaded enforcement data to avoid duplicate API calls */
  data?: EnforcementReadinessResponse | null;
}

export function EnforcementJourneyWidget({ data: preloadedData }: EnforcementJourneyWidgetProps) {
  const [data, setData] = useState<EnforcementReadinessResponse | null>(preloadedData || null);
  const [loading, setLoading] = useState(!preloadedData);

  useEffect(() => {
    if (preloadedData) {
      setData(preloadedData);
      return;
    }

    api
      .getEnforcementReadiness()
      .then((response) => {
        setData(response);
      })
      .catch((err) => {
        console.error("Failed to load enforcement data:", err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [preloadedData]);

  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-6 animate-pulse">
        <div className="h-6 bg-slate-200 rounded w-48 mb-4" />
        <div className="h-24 bg-slate-100 rounded mb-4" />
        <div className="h-16 bg-slate-50 rounded" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-6">
        <div className="text-center text-slate-500 py-8">
          <Shield className="w-10 h-10 mx-auto mb-2 text-slate-300" />
          <p className="text-sm">Unable to load enforcement data</p>
        </div>
      </div>
    );
  }

  const { summary, recommendations } = data;
  const totalDomains = summary.atNone + summary.atQuarantine + summary.atReject;
  const domainsAtEnforcement = summary.atQuarantine + summary.atReject;
  const percentageAtEnforcement = totalDomains > 0
    ? Math.round((domainsAtEnforcement / totalDomains) * 100)
    : 0;

  // Get the top recommendation for the "Next Step" section
  const topRecommendation = recommendations?.[0] || null;

  // Empty state - no domains
  if (totalDomains === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-slate-800 flex items-center gap-2">
            <Shield className="w-5 h-5 text-amber-500" />
            Enforcement Journey
          </h3>
          <Link
            to="/help#enforcement"
            className="text-slate-400 hover:text-amber-600 transition-colors"
            title="Learn about enforcement"
          >
            <HelpCircle className="w-4 h-4" />
          </Link>
        </div>
        <div className="text-center py-6">
          <Shield className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <p className="text-slate-600 font-medium mb-1">No domains yet</p>
          <p className="text-sm text-slate-500 mb-4">
            Add your first domain to start your enforcement journey
          </p>
          <Link
            to="/domains"
            className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium rounded-lg transition-colors"
          >
            Add Domain
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-4 pb-0">
        <h3 className="font-semibold text-slate-800 flex items-center gap-2">
          <Shield className="w-5 h-5 text-amber-500" />
          Enforcement Journey
        </h3>
        <Link
          to="/help#enforcement"
          className="text-slate-400 hover:text-amber-600 transition-colors"
          title="Learn about enforcement"
        >
          <HelpCircle className="w-4 h-4" />
        </Link>
      </div>

      {/* Pipeline Visualization */}
      <div className="p-4 pb-2">
        <div className="flex items-center justify-between gap-2">
          {/* None Stage */}
          <div className="flex-1 text-center">
            <div
              className={`mx-auto w-12 h-12 rounded-xl flex items-center justify-center transition-colors ${
                summary.atNone > 0
                  ? "bg-slate-100 border-2 border-slate-300"
                  : "bg-slate-50 border border-slate-200"
              }`}
            >
              <ShieldAlert
                className={`w-6 h-6 ${
                  summary.atNone > 0 ? "text-slate-600" : "text-slate-300"
                }`}
              />
            </div>
            <div className="mt-2">
              <div className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                Monitor
              </div>
              <div
                className={`text-lg font-bold ${
                  summary.atNone > 0 ? "text-slate-700" : "text-slate-300"
                }`}
              >
                {summary.atNone}
              </div>
            </div>
          </div>

          {/* Arrow 1 */}
          <div className="flex-shrink-0 flex items-center text-slate-300">
            <div className="w-8 h-0.5 bg-slate-200" />
            <ArrowRight className="w-4 h-4 -ml-1" />
          </div>

          {/* Quarantine Stage */}
          <div className="flex-1 text-center">
            <div
              className={`mx-auto w-12 h-12 rounded-xl flex items-center justify-center transition-colors ${
                summary.atQuarantine > 0
                  ? "bg-amber-100 border-2 border-amber-300"
                  : "bg-slate-50 border border-slate-200"
              }`}
            >
              <Shield
                className={`w-6 h-6 ${
                  summary.atQuarantine > 0 ? "text-amber-600" : "text-slate-300"
                }`}
              />
            </div>
            <div className="mt-2">
              <div className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                Quarantine
              </div>
              <div
                className={`text-lg font-bold ${
                  summary.atQuarantine > 0 ? "text-amber-700" : "text-slate-300"
                }`}
              >
                {summary.atQuarantine}
              </div>
            </div>
          </div>

          {/* Arrow 2 */}
          <div className="flex-shrink-0 flex items-center text-slate-300">
            <div className="w-8 h-0.5 bg-slate-200" />
            <ArrowRight className="w-4 h-4 -ml-1" />
          </div>

          {/* Reject Stage */}
          <div className="flex-1 text-center">
            <div
              className={`mx-auto w-12 h-12 rounded-xl flex items-center justify-center transition-colors ${
                summary.atReject > 0
                  ? "bg-emerald-100 border-2 border-emerald-200"
                  : "bg-slate-50 border border-slate-200"
              }`}
            >
              <ShieldCheck
                className={`w-6 h-6 ${
                  summary.atReject > 0 ? "text-emerald-700" : "text-slate-300"
                }`}
              />
            </div>
            <div className="mt-2">
              <div className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                Reject
              </div>
              <div
                className={`text-lg font-bold ${
                  summary.atReject > 0 ? "text-emerald-700" : "text-slate-300"
                }`}
              >
                {summary.atReject}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Next Step Recommendation */}
      {topRecommendation && (
        <div className="mx-4 mb-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
          <div className="flex items-start gap-3">
            <Lightbulb className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-amber-800">
                <span className="font-semibold">Next Step:</span>{" "}
                {topRecommendation.description}
                {topRecommendation.domainName && (
                  <span className="text-amber-600"> ({topRecommendation.domainName})</span>
                )}
              </p>
            </div>
            <Link
              to={topRecommendation.link}
              className="flex-shrink-0 text-xs font-medium text-amber-700 hover:text-amber-900 flex items-center gap-1 transition-colors"
            >
              View
              <ChevronRight className="w-3 h-3" />
            </Link>
          </div>
        </div>
      )}

      {/* All Complete State */}
      {totalDomains > 0 && summary.atReject === totalDomains && (
        <div className="mx-4 mb-3 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
          <div className="flex items-center gap-3">
            <ShieldCheck className="w-5 h-5 text-emerald-700" />
            <p className="text-sm text-emerald-700 font-medium">
              All {totalDomains} domain{totalDomains !== 1 ? "s" : ""} at full enforcement!
            </p>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-t border-slate-100">
        <div className="text-sm text-slate-600">
          <span className="font-semibold text-slate-800">{percentageAtEnforcement}%</span>
          {" "}of domains at enforcement
          {summary.readyToAdvance > 0 && (
            <Link to="/domains?filter=enforcement" className="text-blue-600 hover:underline ml-2">
              • {summary.readyToAdvance} ready to advance
            </Link>
          )}
        </div>
        <Link
          to="/domains?filter=enforcement"
          className="text-sm font-medium text-amber-600 hover:text-amber-700 flex items-center gap-1 transition-colors"
        >
          View All Domains
          <ChevronRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}